import { Injectable, signal } from '@angular/core';

import { BtProvisioningService } from './characteristics';

@Injectable()
export class BtService {
  device?: BluetoothDevice;
  isConnected = signal(false);
  server?: BluetoothRemoteGATTServer;
  service?: BluetoothRemoteGATTService;

  disconnect() {
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }
  }

  async pair() {
    if (this.isConnected()) {
      return;
    }

    this.device = await navigator.bluetooth.requestDevice({
      filters: [
        { services: [BtProvisioningService.SERVICE] },
        { namePrefix: 'HARMONY' },
      ],
    });

    this.server = await this.device.gatt!.connect();
    this.service = await this.server.getPrimaryService(BtProvisioningService.SERVICE);

    this.isConnected.set(true);
  }

  async getSerialNumber() {
    const characteristic = await this.service!.getCharacteristic(BtProvisioningService.SERIAL_NUMBER);

    return new TextDecoder().decode(
      await characteristic.readValue(),
    );
  }

  async getWifiNetworks() {
    const characteristic = await this.service!.getCharacteristic(BtProvisioningService.WIFI_NETWORKS);
    const data = new TextDecoder().decode(
      await characteristic.readValue(),
    );

    const networks = data.trim()
      .split('\n').map((line) => {
        const parts = line.split(' ');
        const strength = +parts.pop()!;
        const ssid = parts.join(' ');

        return { ssid, strength };
      });

    const maxStrength = networks[0].strength;
    const minStrength = networks.at(-1)!.strength;

    networks.forEach(network =>
      network.strength = getSignalLevel(network.strength, minStrength, maxStrength),
    );

    return networks;
  }

  async setWifiCredentials(ssid: string, password: string) {
    const ssidCharacteristic = await this.service!.getCharacteristic(BtProvisioningService.WIFI_SSID);
    const passwordCharacteristic = await this.service!.getCharacteristic(BtProvisioningService.WIFI_PASSWORD);

    await ssidCharacteristic.writeValue(new TextEncoder().encode(ssid));
    await passwordCharacteristic.writeValue(new TextEncoder().encode(password));
  }

  async setMqttCredentials(username: string, password: string) {
    const usernameCharacteristic = await this.service!.getCharacteristic(BtProvisioningService.MQTT_USERNAME);
    const passwordCharacteristic = await this.service!.getCharacteristic(BtProvisioningService.MQTT_PASSWORD);

    await usernameCharacteristic.writeValue(new TextEncoder().encode(username));
    await passwordCharacteristic.writeValue(new TextEncoder().encode(password));
  }

  async isBrokerConfigured() {
    const characteristic = await this.service!.getCharacteristic(BtProvisioningService.BROKER_CONFIGURED);

    const isBrokerConfigured = new TextDecoder().decode(
      await characteristic.readValue(),
    );

    return isBrokerConfigured === '1';
  }
}

export function getSignalLevel(strength: number, min: number, max: number) {
  if (max <= min) {
    return 1;
  }

  const range = max - min;
  const normalized = strength - min;

  if (normalized < range / 4) {
    return 1;
  }

  if (normalized < range / 2) {
    return 2;
  }

  if (normalized < (3 * range) / 4) {
    return 3;
  }

  return 4;
}
